import React, { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { 
  FileSignature, 
  Menu, 
  X, 
  LayoutDashboard, 
  Upload, 
  Settings, 
  History, 
  Users, 
  FileText,
  LogOut,
  User
} from 'lucide-react'
import { useAuth } from '../hooks/useAuth'
import { puedeAbrirRuta } from '../utils/accesoRutas'
import '../styles/layout.css'

const Sidebar = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isCollapsed, setIsCollapsed] = useState(false)

  // Determinar permisos según rol
  const isAdmin = user?.role === 'administrador' || user?.is_admin
  const isSupervisor = user?.role === 'supervisor'
  
  // Función para obtener el nombre del rol en español
  const getRoleName = () => {
    if (isAdmin) return 'Administrador'
    if (isSupervisor) return 'Supervisor'
    return 'Usuario'
  }
  
  const menuItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/editor', label: 'Subir Documentos', icon: Upload },
    { path: '/documentos-pendientes', label: 'Mis Pendientes', icon: FileText },
    { path: '/firmados-pendientes', label: 'Firmados y Pendientes', icon: History },
    { path: '/historial-general', label: 'Historial General', icon: FileText },
    { path: '/users', label: 'Usuarios', icon: Users },
    { path: '/config/signature', label: 'Configuración Firma', icon: Settings },
    { path: '/config', label: 'Configuración Sistema', icon: Settings },
  ].filter((item) => puedeAbrirRuta(item.path, user))

  const isActive = (path) => {
    // Coincidencia exacta
    if (location.pathname === path) {
      return true
    }
    
    // Verificar si la ruta actual empieza con este path + '/'
    // Pero solo activar si no hay otro item del menú más específico que también coincida
    if (location.pathname.startsWith(path + '/')) {
      // Buscar si hay algún otro item del menú que sea más específico y también coincida
      const moreSpecificMatch = menuItems.find(item => {
        // Verificar que este item sea diferente al actual
        if (item.path === path) return false
        // Verificar que el path del item sea más largo (más específico)
        if (item.path.length <= path.length) return false
        // Verificar que el path del item empieza con el path actual + '/'
        if (!item.path.startsWith(path + '/')) return false
        // Verificar que la ruta actual coincide con este item más específico
        return location.pathname === item.path || location.pathname.startsWith(item.path + '/')
      })
      
      // Solo activar si no hay un match más específico
      return !moreSpecificMatch
    }
    
    return false
  }

  const handleLogout = () => {
    logout()
    navigate('/')
  }

  return (
    <>
      {/* Mobile Menu Button - Solo visible en móvil (oculto en desktop) */}
      <button
        className="mobile-menu-btn"
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        aria-label="Toggle mobile menu"
      >
        {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Overlay para mobile - Solo cuando el menú móvil está abierto */}
      {isMobileMenuOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${isMobileMenuOpen ? 'open' : ''} ${isCollapsed ? 'collapsed' : ''}`}>
        {/* Sidebar Header */}
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <button
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                console.log('Toggle clicked, current state:', isCollapsed)
                setIsCollapsed(!isCollapsed)
                setIsMobileMenuOpen(false)
              }}
              className="sidebar-toggle-btn"
              title={isCollapsed ? 'Expandir menú' : 'Contraer menú'}
              type="button"
            >
              <Menu size={24} />
            </button>
            {!isCollapsed && (
              <>
                <FileSignature size={32} />
                <h1>INDUMA</h1>
              </>
            )}
          </div>
          {!isCollapsed && (
            <p className="sidebar-subtitle">Sistema de Firma Electrónica</p>
          )}
        </div>

        {/* Navigation */}
        <nav id="tour-sidebar-nav" className="sidebar-nav" aria-label="Navegación principal">
          {menuItems.map((item) => {
            const Icon = item.icon
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`sidebar-nav-item ${isActive(item.path) ? 'active' : ''}`}
                onClick={() => {
                  setIsMobileMenuOpen(false)
                  if (isCollapsed) {
                    setIsCollapsed(false)
                  }
                }}
                title={isCollapsed ? item.label : ''}
              >
                <Icon size={20} />
                {!isCollapsed && <span>{item.label}</span>}
              </Link>
            )
          })}
        </nav>

        {/* User Section */}
        <div id="tour-sidebar-perfil" className="sidebar-footer">
          {!isCollapsed && (
            <div className="sidebar-user-info">
              <div className="sidebar-user-avatar">
                {user?.nombre?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div className="sidebar-user-details">
                <p className="sidebar-user-name">{user?.nombre || 'Usuario'} {user?.apellido || ''}</p>
                <p className="sidebar-user-email">{user?.email || ''}</p>
                <p className="sidebar-user-role">{getRoleName()}</p>
              </div>
            </div>
          )}
          {isCollapsed && (
            <div className="sidebar-user-avatar-only">
              {user?.nombre?.charAt(0).toUpperCase() || 'U'}
            </div>
          )}
          
          <button 
            className="sidebar-logout-btn" 
            onClick={handleLogout}
            title={isCollapsed ? 'Cerrar Sesión' : ''}
          >
            <LogOut size={20} />
            {!isCollapsed && <span>Cerrar Sesión</span>}
          </button>
        </div>
      </aside>

    </>
  )
}

export default Sidebar

